import { availableAuditEntriesAtom } from '@/global-store';
import { Combobox, type FloatingLabelProps } from '@eye-able/ui';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@helpers/cn';
import { type AuditData } from '@eye-able/types';

interface DomainSelectProps {
  className?: string;
  excludeEntries?: number[];
  fullWidth?: boolean;
  initialEntryId?: string | number;
  labelProps?: FloatingLabelProps;
  onChange?: (entry?: AuditData) => void;
}

interface DomainOption {
  value: string;
  label: string;
}

const DomainSelect = ({
  className,
  excludeEntries = [],
  fullWidth,
  initialEntryId,
  labelProps,
  onChange,
}: DomainSelectProps) => {
  const { t } = useTranslation();
  const [availableAuditEntries] = useAtom(availableAuditEntriesAtom);
  const [searchValue, setSearchValue] = useState('');
  const [entryId, setEntryId] = useState(() =>
    normalizeEntryId(initialEntryId),
  );

  const domains = useMemo(() => {
    return prepareOptions(availableAuditEntries, excludeEntries);
  }, [availableAuditEntries, excludeEntries]);

  const domainMap = useMemo(() => {
    const map = new Map<string, DomainOption>();
    domains.forEach((domain) => map.set(domain.value, domain));

    return map;
  }, [domains]);

  const entryMap = useMemo(() => {
    const map = new Map<string, AuditData>();

    (availableAuditEntries ?? []).forEach((entry) => {
      map.set(String(entry.id), entry);
    });

    return map;
  }, [availableAuditEntries]);

  const selectedDomain = entryId ? (domainMap.get(entryId) ?? null) : null;
  // flag to limit rendered options
  const requiresTypeAhead = domains.length > 99;

  const comboboxItems = useMemo(() => {
    if (!domains.length) return [];

    const normalizedSearch = searchValue.trim().toLowerCase();

    if (!requiresTypeAhead) {
      if (!normalizedSearch) return domains;

      return domains.filter((domain) =>
        domain.label.toLowerCase().includes(normalizedSearch),
      );
    }

    if (!normalizedSearch && selectedDomain) {
      // we need the selected domain to be among the options in order
      // to display it in the combobox
      return [selectedDomain];
    }

    const filtered = domains
      .filter((domain) => domain.label.toLowerCase().includes(normalizedSearch))
      .slice(0, 7);

    return filtered;
  }, [domains, requiresTypeAhead, searchValue, selectedDomain]);

  const handleSelect = (nextValue: string) => {
    if (!nextValue) return;

    setEntryId(nextValue);

    const selectedEntry = entryMap.get(nextValue);
    onChange?.(selectedEntry);
  };

  if (!domains?.length) return null;

  return (
    <div className={cn(className)}>
      <Combobox
        disabled={domains.length === 1}
        fullWidth={fullWidth}
        itemRenderer={(item) => item.label || '-'}
        items={comboboxItems}
        labelProps={{ float: 'top', label: t('domain'), ...labelProps }}
        onSearchChange={setSearchValue}
        onSelect={handleSelect}
        placeholder={t('selectDomain')}
        search={searchValue}
        filter={filterFn}
        value={entryId ?? ''}
      />
    </div>
  );
};

function filterFn(value: string, query: string) {
  if (value.includes(query)) return 1;
  return 0;
}

function prepareOptions(
  availableAuditEntries: AuditData[] | undefined,
  excludeEntries: number[],
) {
  if (!availableAuditEntries) return [];

  const result: Array<DomainOption> = [];
  const countMap: Record<string, number> = {};
  const excludeSet = new Set((excludeEntries ?? []).map(String));

  availableAuditEntries.forEach((entry) => {
    const normalizedId = String(entry.id);

    if (excludeSet.has(normalizedId)) return;

    let label = entry.domain;

    // show the full domain only for admins
    if (availableAuditEntries.length < 99) {
      label = shortenDomain(label);
    }

    if (countMap[label]) {
      countMap[label]++;
      label = `${label} (${countMap[label]})`;
    } else {
      countMap[label] = 1;
    }

    result.push({ value: normalizedId, label });
  });

  return result;
}

function shortenDomain(domain: string): string {
  let label = domain;

  if (/^https?:\/\//.test(label)) {
    label = label.replace(/^(http|https):\/\//, '');
  }

  if (label.endsWith('/')) {
    label = label.slice(0, -1);
  }

  return label;
}

function normalizeEntryId(
  value: string | number | undefined,
): string | undefined {
  return value == undefined ? undefined : String(value);
}

export default DomainSelect;
